import { type DocNode } from '@atlaskit/adf-schema';
import { fg } from '@atlaskit/platform-feature-flags';
import type { Metric } from '@atlassian/browser-metrics/types';
import { isCreatableAction } from '@atlassian/conversation-assistant-action-helpers/utils';
import { isAdfContent } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import type { ExperienceTrackerAPI } from '@atlassian/conversation-assistant-instrumentation/main';
import type {
	CitedProductList,
	SourceProducts,
} from '@atlassian/conversation-assistant-instrumentation/types/analytics';
import {
	type AnswerGenerationType,
	type AssistantMessage,
	type HumanMessage,
	isActionExecutedSuccessfully,
	isActionResolutionCancelled,
	isStreamResponse,
	type LastAgentMessageStatus,
	type Sources,
	type StreamActionPart,
	type StreamAnswerPart,
	type StreamDynamicLayoutSubAgent,
	type StreamResponse,
	type StreamTrace,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	type MessageAction,
	type MessageActionInvocationStatus,
	type MessageActionPayloadContext,
} from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import {
	createHumanMessage,
	getId,
	addActionPart as serviceAddActionPart,
	addAnswerPart as serviceAddAnswerPart,
	removeSensitiveData as serviceRemoveSensitiveData,
} from '@atlassian/conversation-assistant-service/services/assistance-service/utils/data';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import type {
	ContentContext,
	ContentType,
	useChatContextProvider,
} from '@atlassian/conversation-assistant-store/controllers/chat-context/store';
import {
	type ChatStatus,
	type StoreError,
} from '@atlassian/conversation-assistant-store/controllers/chat-signals';
import {
	type AnswerNowStatus,
	CHAT_STORE_STATUS,
	type ChatStorePrivateState,
	type Conversation,
	isStoreAssistant,
	isStoreHuman,
	isStoreMessage,
	type MessageTemplate,
	type StoreActionLoggableName,
	type StoreAssistant,
	type StoreChatItem,
	type StoreHuman,
	type StoreMessage,
	type Trace,
	type StoreMemoryUpdate,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { extractOutboundAuthLink } from '@atlassian/smart-creation/auth-link';

import { extractProductNameFromARI } from '../../common/utils/ari';
import type { StreamLifecycleState } from '../bridge-api/streaming-lifecycle';

import {
	DeepResearchError,
	MessageActionError,
	NoActionInvokedError,
	StreamErrorException,
	StreamGeneratorError,
} from './error-classes';
import type { ChatStoreContainerProps } from './types';

export const generateId = (): string => String(Math.floor(Math.random() * 1000000));

export const createBroadcastStreamLifecycle = ({
	broadcastStreamStatus,
	chatContextStoreActions,
	contentContextValue,
	targetId,
	assistantMessageId,
}: {
	broadcastStreamStatus?: (
		status: StreamLifecycleState,
		streamId: string,
		content?: { id: string | number; type: ContentType },
	) => void;
	chatContextStoreActions: ReturnType<typeof useChatContextProvider>;
	contentContextValue: ContentContext;
	targetId?: string | number;
	assistantMessageId: string;
}) => {
	return (status: StreamLifecycleState) => {
		let contentTarget;
		// Only use targetId when aifc_multiple_context is enabled
		if (fg('aifc_multiple_context')) {
			contentTarget = chatContextStoreActions.getTarget(contentContextValue, targetId);
		} else {
			contentTarget = chatContextStoreActions.getTarget(contentContextValue);
		}

		broadcastStreamStatus?.(
			status,
			assistantMessageId,
			contentTarget ? { id: contentTarget.id, type: contentTarget.type as ContentType } : undefined,
		);
	};
};

export const getStoreAssistantMessageContent = (
	messages: StoreChatItem[],
	messageId: string,
): string => {
	const assistantMessage = messages.find(
		(chatItem) => isStoreAssistant(chatItem) && getId(chatItem.content) === messageId,
	) as StoreAssistant | undefined;
	if (!assistantMessage) {
		return '';
	}
	const content = assistantMessage.content.message.message.content;
	return typeof content === 'string' ? content : '';
};

export const clearStoreAssistantMessageContent = (messages: StoreChatItem[], messageId: string) => {
	return messages.map((chatItem) => {
		if (isStoreAssistant(chatItem) && getStoreMessageId(chatItem) === messageId) {
			return {
				...chatItem,
				content: {
					...chatItem.content,
					message: {
						...chatItem.content.message,
						message: {
							...chatItem.content.message.message,
							content: '',
						},
					},
					streamingComplete: false,
				},
			};
		}

		return chatItem;
	});
};

export const updateStoreAssistantMessageAnswerGenerationType = (
	messages: StoreChatItem[],
	messageId: string,
	answerGenerationType: AnswerGenerationType,
) => {
	return messages.map((chatItem) => {
		if (isStoreAssistant(chatItem) && getStoreMessageId(chatItem) === messageId) {
			return {
				...chatItem,
				content: {
					...chatItem.content,
					message: {
						...chatItem.content.message,
						message: {
							...chatItem.content.message.message,
						},
					},
					streamingComplete: false,
				},
				answerGenerationType: answerGenerationType,
			};
		}

		return chatItem;
	});
};

export const updateStoreAssistantMessageAnswerNowStatus = (
	messages: StoreChatItem[],
	messageId: string,
	answerNowStatus: AnswerNowStatus | undefined,
) => {
	return messages.map((chatItem) => {
		if (isStoreAssistant(chatItem) && getStoreMessageId(chatItem) === messageId) {
			return {
				...chatItem,
				answerNowStatus,
			};
		}

		return chatItem;
	});
};

export const clearAnswerNowStatusForMessageIfSet = (
	conversationId: string,
	messageId: string,
	getMessages: (id: string) => StoreChatItem[],
	setMessages: (id: string, messages: StoreChatItem[]) => void,
): void => {
	const currentMessages = getMessages(conversationId);
	const assistantMessage = currentMessages.find(
		(msg) => isStoreAssistant(msg) && getStoreMessageId(msg) === messageId,
	);
	if (
		assistantMessage &&
		'answerNowStatus' in assistantMessage &&
		assistantMessage.answerNowStatus
	) {
		setMessages(
			conversationId,
			updateStoreAssistantMessageAnswerNowStatus(currentMessages, messageId, undefined),
		);
	}
};

export const addSubAgentMessageToStoreAssistantMessage = (
	messages: StoreChatItem[],
	messageId: string,
	subAgentMessage: StreamDynamicLayoutSubAgent,
) => {
	return messages.map((chatItem) => {
		if (isStoreAssistant(chatItem) && getStoreMessageId(chatItem) === messageId) {
			return {
				...chatItem,
				content: {
					...chatItem.content,
					message: {
						...chatItem.content.message,
						message: {
							...chatItem.content.message.message,
						},
					},
					streamingComplete: false,
				},
				dynamicLayoutSubAgentMessages: [
					...(chatItem.dynamicLayoutSubAgentMessages || []),
					subAgentMessage,
				],
			};
		}

		return chatItem;
	});
};

export const getTraceFromStreamMessage = (streamMessage: StreamTrace): Trace => {
	return {
		messageContent: streamMessage.message?.content,
		messageTemplate: streamMessage.message?.message_template as MessageTemplate,
	};
};

export const createStoreError = (
	error: unknown,
	prefix: string = '',
	actionLoggableNameIfActionExecutionFailed?: StoreActionLoggableName,
): StoreError => {
	let status_code, message_template, timeout;
	const message =
		error instanceof Error ? error.message : typeof error === 'string' ? error : 'Unknown error';
	let prefixedMessage = prefix ? `${prefix}: ${message}` : message;

	let errorType: string | undefined;
	let traceId: string | undefined;

	if (error instanceof StreamErrorException) {
		status_code = error.detail.message.status_code;
		message_template = error.detail.message.message_template;
		timeout = error.detail.metadata?.timeout;
		errorType = 'StreamErrorException';
		traceId = error.errorAttributes.traceId;
	}

	if (error instanceof ApiError) {
		status_code = error.errorAttributes.statusCode;
		message_template = error.errorAttributes.errorCode;
		errorType = 'ApiError';
		traceId = error.errorAttributes.traceId;
	}

	if (error instanceof MessageActionError) {
		// Prefer showing only the raw API error message (without codes) to end users
		const rawErrorMessage =
			error.cause &&
			'errorAttributes' in error.cause &&
			typeof error.cause.errorAttributes === 'object' &&
			error.cause.errorAttributes !== null &&
			'rawErrorMessage' in error.cause.errorAttributes
				? (error.cause.errorAttributes as { rawErrorMessage?: string }).rawErrorMessage
				: undefined;

		if (typeof rawErrorMessage === 'string') {
			prefixedMessage = prefix ? `${prefix}: ${rawErrorMessage}` : rawErrorMessage;
		}
	}

	if (error instanceof StreamGeneratorError) {
		errorType = 'StreamGeneratorError';
		traceId = error.errorAttributes.traceId;
	}

	if (error instanceof DeepResearchError) {
		message_template = error.message_template;
		errorType = 'DeepResearchError';
		traceId = error.errorAttributes.traceId;
	}

	return {
		type: 'ERROR',
		content: prefixedMessage,
		time_created: new Date().toLocaleString(),
		status_code,
		message_template,
		timeout,
		id: generateId(),
		errorType,
		traceId,
		storeActionExecutionError: actionLoggableNameIfActionExecutionFailed
			? {
					actionLoggableName: actionLoggableNameIfActionExecutionFailed,
				}
			: undefined,
	};
};

export const createStoreHumanWithContent = (content: HumanMessage): StoreHuman => ({
	type: 'HUMAN',
	content,
});

export const createStoreHuman = (...args: Parameters<typeof createHumanMessage>): StoreHuman => ({
	type: 'HUMAN',
	content: createHumanMessage(...args),
});

export const createStoreAssistantWithContent = (content: StreamResponse): StoreAssistant => ({
	type: 'ASSISTANT',
	content,
});

export const createStoreAssistant = (args: {
	id: string;
	content: string | DocNode;
	agentNamedId?: string;
	conversationId?: string;
}): StoreAssistant => ({
	type: 'ASSISTANT',
	content: {
		type: 'FINAL_RESPONSE',
		message: {
			message: {
				role: 'ASSISTANT',
				id: args.id,
				conversation_channel_id: args.conversationId,
				content: args.content,
				time_created: new Date().toISOString(),
				user_ari: 'unknown',
				plugin_invocations: [],
				author: {
					named_id: args.agentNamedId || 'ai_mate_agent',
					name: 'unknown',
					actor_type: 'AGENT',
					ari: null,
					id: 'unknown',
				},
			},
		},
	},
});

const getStoreMessageId = (storeMessage: StoreMessage): string => {
	if (isStoreAssistant(storeMessage)) {
		return getId(storeMessage.content);
	}
	return storeMessage.content.id;
};

export const findStoreMessageIndex = (chat: StoreChatItem[], messageId?: string) =>
	chat.findIndex((storeMessage) =>
		isStoreMessage(storeMessage) && messageId
			? getStoreMessageId(storeMessage) === messageId
			: true,
	);

const updateStoreMessage = ({
	chat,
	messageId,
	updateFunction,
	updateType,
	useIndex,
}: {
	chat: StoreChatItem[];
	messageId: string;
	updateFunction: (storeMessage: StoreMessage) => StoreMessage;
	updateType: string;
	useIndex?: number;
}): StoreChatItem[] => {
	// If the chat is empty, return it as is - prevents the 'in' of 'undefined' errors we were seeing
	// TODO: Take this into account with upcoming refactor
	if (!chat.length) {
		return chat;
	}

	const targetIndex = useIndex || findStoreMessageIndex(chat, messageId);
	const targetStoreMessage = chat[targetIndex];
	if (targetIndex < 0 || !isStoreMessage(targetStoreMessage)) {
		throw new Error(`Attempting to update a non StoreMessage (${updateType})`);
	}
	if (getStoreMessageId(targetStoreMessage) !== messageId) {
		throw new Error(`Attempting to update a store message at the wrong index (${updateType})`);
	}
	const newStoreMessage = updateFunction(targetStoreMessage);
	const newChat = [...chat];
	newChat.splice(targetIndex, 1, newStoreMessage);
	return newChat;
};

const updateStoreAssistant = ({
	chat,
	promptId,
	updateFunction,
	updateType,
	useIndex,
}: {
	chat: StoreChatItem[];
	promptId: string;
	updateFunction: (storeMessage: StoreAssistant) => StoreAssistant;
	updateType: string;
	useIndex?: number;
}): StoreChatItem[] =>
	updateStoreMessage({
		chat,
		messageId: promptId,
		updateFunction: (storeMessage): StoreAssistant => {
			if (!isStoreAssistant(storeMessage)) {
				throw new Error(`Attempting to update a non StoreAssistant (${updateType})`);
			}
			return updateFunction(storeMessage);
		},
		updateType,
		useIndex,
	});

export const addAnswerPart = ({
	chat,
	answerId,
	answerPart,
	useIndex,
}: {
	chat: StoreChatItem[];
	answerId: string;
	answerPart: StreamAnswerPart;
	useIndex?: number;
}): StoreChatItem[] =>
	updateStoreAssistant({
		chat,
		promptId: answerId,
		updateFunction: (storeAssistant): StoreAssistant => ({
			...storeAssistant,
			content: {
				...serviceAddAnswerPart(storeAssistant.content, answerPart),
				streamingComplete: false,
			},
		}),
		updateType: 'Add Answer Part',
		useIndex,
	});

export const addAnswerPartWithMarkdownData = ({
	chat,
	answerId,
	answerPart,
	markdownData,
	useIndex,
}: {
	chat: StoreChatItem[];
	answerId: string;
	answerPart: StreamAnswerPart;
	markdownData: MarkdownData;
	useIndex?: number;
}): StoreChatItem[] => {
	const { key, content, loading } = markdownData;
	const loadingKey = `${key}-loading`;

	return updateStoreAssistant({
		chat,
		promptId: answerId,
		updateFunction: (storeAssistant): StoreAssistant => ({
			...storeAssistant,
			content: {
				...serviceAddAnswerPart(storeAssistant.content, answerPart),
				streamingComplete: false,
			},
			markdownData: {
				...storeAssistant.markdownData,
				[key]: (storeAssistant.markdownData?.[key] || '') + content,
				[loadingKey]: loading ? 'true' : 'false',
			},
		}),
		updateType: 'Add Answer Part With Markdown Data',
		useIndex,
	});
};

export const addActionPart = ({
	chat,
	messageId,
	actionPart,
	useIndex,
	actionUpdaterOverride,
}: {
	chat: StoreChatItem[];
	messageId: string;
	actionPart: StreamActionPart;
	useIndex?: number;
	actionUpdaterOverride?: (prevAction: MessageAction, nextAction: MessageAction) => MessageAction;
}): StoreChatItem[] => {
	const findMessage = chat.findIndex(
		(message) => isStoreAssistant(message) && getId(message.content) === messageId,
	);
	const actionIndex = findMessage > 0 ? findMessage : chat.length - 1;
	const index = useIndex || actionIndex;

	return updateStoreAssistant({
		chat,
		promptId: messageId,
		updateFunction: (storeAssistant): StoreAssistant => ({
			...storeAssistant,
			content: {
				...serviceAddActionPart(storeAssistant.content, actionPart, actionUpdaterOverride),
			},
		}),
		updateType: 'Add Action Part',
		useIndex: index,
	});
};

// Helper function to extract data by type and index from markdownData
export const getMarkdownDataByType = ({
	markdownData,
	type,
	index,
}: {
	markdownData: Record<string, string> | undefined;
	type: 'sculptor' | 'expand' | 'chart';
	index: number;
}): string | undefined => {
	return markdownData?.[`${type}-${index}`];
};

export const replaceAnswerData = ({
	chat,
	answerId,
	content,
	useIndex,
	humanMessageId,
	markdownData,
	onMessageIdChanged,
}: {
	chat: StoreChatItem[];
	answerId: string;
	content: StreamResponse;
	useIndex?: number;
	humanMessageId?: string;
	markdownData?: Record<string, string>;
	onMessageIdChanged?: (oldId: string, newId: string) => void;
}): StoreChatItem[] =>
	updateStoreAssistant({
		chat,
		promptId: answerId,
		updateFunction: (storeMessage): StoreAssistant => {
			const oldId = getStoreMessageId(storeMessage);
			const newId = content.message.message.id;

			if (oldId !== newId) {
				onMessageIdChanged?.(oldId, newId);
			}

			return {
				...storeMessage,
				content: {
					...content,
					streamingComplete: false,
				},
				humanMessageId,
				markdownData,
			};
		},
		updateType: 'Replace Answer Data',
		useIndex,
	});

export const updateHumanMessageId = ({
	chat,
	oldId,
	newId,
}: {
	chat: StoreChatItem[];
	oldId: string;
	newId: string;
}): StoreChatItem[] =>
	chat.map((message) => {
		if (isStoreHuman(message) && message.content.id === oldId) {
			return {
				...message,
				content: {
					...message.content,
					id: newId,
				},
			};
		}
		return message;
	});

export const updateStoreAssistantMessageId = ({
	chat,
	oldId,
	newId,
}: {
	chat: StoreChatItem[];
	oldId: string;
	newId: string;
}): StoreChatItem[] =>
	updateStoreAssistant({
		chat,
		promptId: oldId,
		updateFunction: (storeAssistant) => ({
			...storeAssistant,
			content: {
				...storeAssistant.content,
				message: {
					...storeAssistant.content.message,
					message: {
						...storeAssistant.content.message.message,
						id: newId,
					},
				},
			},
		}),
		updateType: 'Update Store Assistant Message ID',
	});

export const markStreamingMessagesAsComplete = (chat: StoreChatItem[]): StoreChatItem[] =>
	chat.map((message) => {
		if (isStoreAssistant(message) && !message.content.streamingComplete) {
			return {
				...message,
				content: { ...message.content, streamingComplete: true },
				// Clear answerNowStatus when streaming completes (success, error, or abort)
				answerNowStatus: undefined,
			};
		}
		return message;
	});

/**
 * Returns the message pointed by messageId plus the number of previous messages set by messageCount.
 * If @param startFromMessageId is not provided, the last n messages are returned.
 * Example: messageCount = 1 => array with the target message plus 1 previous message.
 */
export const getMessageHistory = (
	chat: StoreChatItem[],
	startFromMessageId?: string,
	messageCount: number = 1,
) => {
	const messageIndex = findStoreMessageIndex(chat, startFromMessageId);
	if (messageIndex < 0) {
		return [];
	}
	const messageHistory = chat.slice(Math.max(0, messageIndex - messageCount), messageIndex + 1);

	return messageHistory;
};

export const clearStatus = ({
	lastAgentMessageStatus,
}: {
	lastAgentMessageStatus?: LastAgentMessageStatus;
}): Partial<Conversation> => ({
	status: CHAT_STORE_STATUS.idle,
	traces: [],
	storeAbortController: undefined,
	...(lastAgentMessageStatus !== undefined && { lastAgentMessageStatus }),
});

export const removeSensitiveData = (message: StoreAssistant): StoreAssistant => ({
	...message,
	content: serviceRemoveSensitiveData(message.content),
});

export const getConversation = (
	state: ChatStorePrivateState,
	id: string,
): Conversation | undefined => {
	return state.conversations[id];
};

export const checkActiveConversationMatchesSource = (
	state: ChatStorePrivateState,
	id?: string,
): boolean => {
	return !!id && state.conversations[state.currentConversationId]?.sourceId === id;
};

export const getLastHumanMessageFromConversation = (
	messages: StoreChatItem[],
): StoreHuman | undefined => {
	return findLast(messages, isStoreHuman) as StoreHuman | undefined;
};

export const getLastAgentMessageID = (messages: StoreChatItem[]): string | undefined => {
	try {
		const last = findLast(messages, isStoreAssistant) as StoreAssistant | undefined;
		return last?.content.message.message.id;
	} catch {
		return undefined;
	}
};

export const getLastRequestIdFromConversation = (messages: StoreChatItem[]): string | undefined => {
	return getLastHumanMessageFromConversation(messages)?.content.requestId;
};

export const getAgentMessageById = (
	messages: StoreChatItem[],
	messageId: string,
): { result: StoreAssistant | false; index: number } => {
	let index = -1;
	const result = messages.find((item, i) => {
		index = i;
		return isStoreAssistant(item) && getId(item.content) === messageId;
	});
	return result
		? {
				result: result as StoreAssistant,
				index,
			}
		: { result: false, index };
};

export const findPreviousByTypeFromId = <T extends StoreChatItem>({
	messages,
	messageId,
	searchType,
}: {
	messages: StoreChatItem[];
	messageId: string;
	searchType: StoreChatItem['type'];
}): T | undefined => {
	const startIndex = messages.findIndex((item) => {
		switch (item.type) {
			case 'HUMAN':
				return item.content.id === messageId;
			case 'ASSISTANT':
				return item.content.message.message.id === messageId;
			case 'FOLLOW_UP':
				return String(item.id) === messageId;
			default:
				return false;
		}
	});

	for (let i = startIndex; i >= 0; i--) {
		if (messages[i].type === searchType) {
			return messages[i] as T;
		}
	}

	return undefined;
};

export const getAssociatedHumanMessageId = (
	messages: StoreChatItem[],
	agentMessageId: string,
): string | undefined => {
	try {
		const agentMessage = getAgentMessageById(messages, agentMessageId).result;
		if (agentMessage && isStoreAssistant(agentMessage)) {
			const foundId = findPreviousByTypeFromId<StoreHuman>({
				messages,
				messageId: agentMessageId,
				searchType: 'HUMAN',
			})?.content.id;
			return foundId ? String(foundId) : undefined;
		}
	} catch {
		return undefined;
	}
};

// Request ID is used as the most consistent identifying value for the E2E life cycle of a message
// Since we don't have the true network messageId until the FINAL_RESPONSE from the stream
// This allows us to identify the funnel of events from message send through to abort, dismiss, error, success, etc
export const addRequestIdToLatestHumanMessage = (chat: StoreChatItem[], requestId?: string) => {
	const lastHumanMessage = findLast(chat, isStoreHuman) as StoreHuman | undefined;

	// Skip if there is no last human message or if the requestId is already set
	if (!lastHumanMessage || !requestId || !!lastHumanMessage.content.requestId) {
		return chat;
	}
	const newHumanMessage = {
		...lastHumanMessage,
		content: {
			...lastHumanMessage.content,
			requestId,
		},
	};
	const newChat = [...chat];
	newChat[newChat.lastIndexOf(lastHumanMessage)] = newHumanMessage;
	return newChat;
};

export const isEmptyConversation = (conversationId?: string): boolean =>
	conversationId === EMPTY_CONVERSATION_ID || conversationId === undefined;

export const isChatLoading = (
	chatStatus: ChatStatus,
	conversationStatus?: CHAT_STORE_STATUS | undefined,
): boolean => {
	return chatStatus === 'launching' || conversationStatus === CHAT_STORE_STATUS.loadingChat;
};

export const isMessageLastOfType = ({
	chat,
	type,
	index,
}: {
	chat: StoreChatItem[];
	type: StoreChatItem['type'];
	index: number;
}): boolean => {
	const lastIndex = chat.map((item) => item.type).lastIndexOf(type);
	return lastIndex === index;
};

export const getFirstMessageOfType = <T extends StoreChatItem>(
	chat: StoreChatItem[],
	type: T['type'],
): T | undefined => {
	const lastIndex = chat.map((item) => item.type).indexOf(type);

	if (lastIndex >= 0) {
		return chat[lastIndex] as T | undefined;
	}
};

// findLast polyfill since it's a newer ECMAScript feature
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/findLast
export const findLast = <T>(array: T[], predicate: (item: T) => boolean) => {
	for (let i = array.length - 1; i >= 0; i--) {
		if (predicate(array[i])) {
			return array[i];
		}
	}
	return undefined;
};

// findLastIndex polyfill since it's a newer ECMAScript feature
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/findLastIndex
export const findLastIndex = <T>(array: T[], predicate: (item: T) => boolean) => {
	for (let i = array.length - 1; i >= 0; i--) {
		if (predicate(array[i])) {
			return i;
		}
	}
	return -1;
};

export const getSourceAgentId = (
	messages: StoreChatItem[],
	conversation?: Conversation,
): string | undefined => {
	const assistantMessage = messages.find((message) => isStoreAssistant(message)) as
		| StoreAssistant
		| undefined;

	return conversation?.sourceAgentId ?? assistantMessage?.content.message.message.author_id;
};

// Used to generate a unique `singleInstrumentationID` for tracking Question & Answer pairings
// before a response has been received from the server
export const djb2Hash = (timeStamp: string, conversationId: string) => {
	// More detail on djb2 hash function https://theartincode.stanis.me/008-djb2/
	let hash = 5381;

	if (!timeStamp || !conversationId) {
		return hash;
	}

	const concat = conversationId + timeStamp;

	for (let i = 0; i < concat.length; i++) {
		// Update the hash value
		hash = (hash << 5) + hash + concat.charCodeAt(i); // hash * 33 + c
	}
	// Ensure the hash is a positive integer
	return hash >>> 0;
};

export const generateTmpMessageId = (conversationId: string): string =>
	String(djb2Hash(new Date().toISOString(), conversationId));

export const constructCitedProductList = (sources: Sources | undefined): CitedProductList => {
	if (!sources) {
		return {};
	}

	const citedProductList: CitedProductList = {};
	sources.forEach((source) => {
		const productName = extractProductNameFromARI(source.ari);
		if (productName) {
			citedProductList[productName] = (citedProductList[productName] ?? 0) + 1;
		}
	});

	return citedProductList;
};

export const constructSourceProducts = (sources: Sources | undefined): SourceProducts => {
	if (!sources) {
		return {};
	}

	const products: SourceProducts = {};
	sources.forEach((source) => {
		const productName = source.product;
		if (productName) {
			products[productName] = (products[productName] ?? 0) + 1;
		}
	});

	return products;
};

// Helper function to escape special regex characters
function escapeRegExp(string: string) {
	return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export const extractMarkdownContent = ({
	streamMessage,
	boundaryStart,
	boundaryEnd,
	replacementContent,
}: {
	streamMessage: StreamResponse | StreamAnswerPart;
	boundaryStart: string;
	boundaryEnd: string;
	replacementContent?: string;
}): {
	processedStreamMessage: StreamResponse | StreamAnswerPart;
	boundaryContents: string[];
} => {
	let processedStreamMessage = streamMessage;
	let boundaryContents: string[] = [];

	let messageContent = isStreamResponse(streamMessage)
		? streamMessage.message.message.content
		: streamMessage.message.content;

	if (!messageContent) {
		return {
			processedStreamMessage,
			boundaryContents,
		};
	}

	if (typeof messageContent === 'string') {
		const regex = new RegExp(
			`${escapeRegExp(boundaryStart)}(.*?)${escapeRegExp(boundaryEnd)}`,
			'gs',
		);
		let match;
		let newContent = messageContent;
		let found = false;
		while ((match = regex.exec(messageContent)) !== null) {
			boundaryContents.push(match[1]);
			newContent = newContent.replace(match[0], replacementContent ?? '');
			found = true;
		}
		if (found) {
			if (isStreamResponse(processedStreamMessage)) {
				processedStreamMessage.message.message.content = newContent;
			} else {
				processedStreamMessage.message.content = newContent;
			}
		}
	}

	return {
		processedStreamMessage,
		boundaryContents,
	};
};

export const isValidAdfJsonString = (jsonString: string): boolean => {
	try {
		const parsed = JSON.parse(jsonString);
		if (isAdfContent(parsed)) {
			return true;
		}

		return false;
	} catch {
		return false;
	}
};

const serviceDeskSourceRegex = /https:\/\/[a-zA-Z0-9_.-]+\/servicedesk\/customer\//g;

export const containsServiceDeskSource = (pluginOutput: string): boolean => {
	return serviceDeskSourceRegex.test(pluginOutput);
};

type MarkdownData = {
	key: string;
	content: string;
	loading: boolean;
};

type StreamableComponentConfig = {
	key: string;
	startTag: string;
	endTag: string;
	placeholderTag: string;
	isEnabled: boolean;
};

export const getStreamableComponents = (): StreamableComponentConfig[] => [
	{
		key: 'expand',
		startTag: '▞',
		endTag: '▚',
		placeholderTag: '[^expand^]',
		isEnabled: fg('di_chat_stream_expands'),
	},
	{
		key: 'chart',
		startTag: '◴',
		endTag: '◶',
		placeholderTag: '[^chart^]',
		isEnabled: true,
	},
	{
		key: 'lumina',
		startTag: '<lumina>',
		endTag: '</lumina>',
		placeholderTag: '\n\n[^lumina^]\n\n',
		isEnabled: fg('rovo_chat_enable_inline_lumina'),
	},
];

/**
 * Processes stream message content for markdown boundary functionality
 * Rovo streams its response one token at a time. Therefore, openTag and closeTag
 * must be a single token (e.g. unicode char) in order for this function to work
 */
export const processStreamMarkdownComponent = ({
	streamMessage,
	currentLoadingKey,
	previousKey,
	componentType,
}: {
	streamMessage: StreamAnswerPart;
	currentLoadingKey: string | null;
	previousKey: string | null;
	componentType: string;
}): {
	processedStreamMessage: StreamAnswerPart;
	currentLoadingKey: string | null;
	previousKey: string | null;
	markdownData: MarkdownData | null;
} => {
	const componentConfig = getStreamableComponents().find((c) => c.key === componentType);
	if (!componentConfig) {
		return {
			processedStreamMessage: streamMessage,
			currentLoadingKey: currentLoadingKey,
			previousKey: previousKey,
			markdownData: null,
		};
	}

	const { startTag, endTag, placeholderTag, key } = componentConfig;
	const messageContent = streamMessage.message.content;
	let streamingMarkdownContent: string = '';
	let newLoadingKey = currentLoadingKey;
	let newPreviousKey = previousKey;
	let markdownData: MarkdownData | null = null;

	if (typeof messageContent === 'string') {
		const hasCloseTag = messageContent.includes(endTag);
		if (hasCloseTag && currentLoadingKey && currentLoadingKey.startsWith(`${key}-`)) {
			const [contentBeforeCloseTag, contentAfterCloseTag] = messageContent.split(endTag);

			streamingMarkdownContent = contentBeforeCloseTag;

			streamMessage.message.content = contentAfterCloseTag;

			markdownData = {
				key: currentLoadingKey,
				content: streamingMarkdownContent,
				loading: false,
			};
			newPreviousKey = currentLoadingKey;

			newLoadingKey = null;
		} else if (messageContent.includes(startTag) && !currentLoadingKey) {
			const currentCount =
				previousKey == null ? -1 : parseInt(previousKey!.substring(`${key}-`.length), 10);
			newLoadingKey = `${key}-${currentCount + 1}`;

			const [contentBeforeOpenTag, contentAfterOpenTag] = messageContent.split(startTag);

			streamMessage.message.content = contentBeforeOpenTag + placeholderTag;

			streamingMarkdownContent = contentAfterOpenTag;

			markdownData = {
				key: newLoadingKey,
				content: streamingMarkdownContent,
				loading: true,
			};
			newPreviousKey = newLoadingKey;
		} else if (currentLoadingKey) {
			streamingMarkdownContent = messageContent;
			streamMessage.message.content = '';

			markdownData = {
				key: currentLoadingKey,
				content: streamingMarkdownContent,
				loading: true,
			};
			newPreviousKey = currentLoadingKey;
		}
	}

	return {
		processedStreamMessage: streamMessage,
		currentLoadingKey: newLoadingKey,
		previousKey: newPreviousKey,
		markdownData: markdownData,
	};
};

export const createStreamMarkdownProcessor = () => {
	let currentLoadingKeys: Record<string, string | null> = {};
	let previousKeys: Record<string, string | null> = {};

	return (
		streamMessage: StreamAnswerPart,
	): {
		processedStreamMessage: StreamAnswerPart;
		markdownData: MarkdownData | null;
		processedComponentType: string | null;
	} => {
		let processedStreamMessage = streamMessage;
		let newCurrentLoadingKeys = { ...currentLoadingKeys };
		let newPreviousKeys = { ...previousKeys };
		let markdownData: MarkdownData | null = null;
		let processedComponentType: string | null = null;

		for (const component of getStreamableComponents()) {
			if (!component.isEnabled) {
				continue;
			}

			const currentLoadingKey = currentLoadingKeys[component.key] || null;
			const previousKey = previousKeys[component.key] || null;

			const result = processStreamMarkdownComponent({
				streamMessage: processedStreamMessage,
				currentLoadingKey,
				previousKey,
				componentType: component.key,
			});

			if (result.markdownData) {
				processedStreamMessage = result.processedStreamMessage;
				newCurrentLoadingKeys[component.key] = result.currentLoadingKey;
				newPreviousKeys[component.key] = result.previousKey;
				markdownData = result.markdownData;
				processedComponentType = component.key;
				break; // Only process one component type per call
			}
		}

		currentLoadingKeys = newCurrentLoadingKeys;
		previousKeys = newPreviousKeys;

		return {
			processedStreamMessage,
			markdownData,
			processedComponentType,
		};
	};
};

/**
 * Determines the invocation status of an assistant message action based on its execution result and context.
 *
 * @param message - The assistant message to evaluate.
 * @param context - The context payload associated with the message action.
 * @returns The invocation status, which can be 'errored' if the action was not executed successfully,
 *          'cancelled' if the resolution was cancelled, or 'finished' if the action completed successfully.
 */
export function deriveInvocationStatus(
	message: AssistantMessage,
	context: MessageActionPayloadContext,
): MessageActionInvocationStatus {
	if (!isActionExecutedSuccessfully(message)) {
		return 'errored';
	}
	return isActionResolutionCancelled(context) ? 'cancelled' : 'finished';
}

/**
 * Classifies whether an error thrown while processing the stream originated on the client/network side.
 */
export const isClientSideStreamingError = (error: unknown): boolean => {
	if (!error) {
		return false;
	}

	// Server-sent error chunks are wrapped as StreamErrorException and should be excluded
	if (error instanceof StreamErrorException) {
		return false;
	}

	// Any ApiError (or object exposing errorAttributes.statusCode) comes from the HTTP layer, so exclude
	if (error instanceof ApiError || (error as any)?.errorAttributes?.statusCode) {
		return false;
	}

	const message = (error as any)?.message as string | undefined;
	// When the backend terminates the stream abruptly we get a JSON parsing error, so we treat it as server-side
	// See onversation-assistant-service/src/services/assistance-service/read-json-stream/index.ts
	if (typeof message === 'string' && message.startsWith('Error Processing Stream Data')) {
		return false;
	}

	// Whatever remains is considered a client/network error
	return true;
};

export const findLastAssistantAction = (
	messages: StoreChatItem[],
	predicate: (action: MessageAction) => boolean,
) => {
	for (let i = messages.length - 1; i >= 0; i--) {
		const msg = messages[i];
		if (isStoreAssistant(msg)) {
			const actionParts = msg.content.message?.message?.actions ?? [];
			const action = findLast(actionParts, predicate);

			if (action) {
				return action;
			}
		}
	}

	return undefined;
};

export const handleStagingAreaActionValidation = (
	allMessages: Conversation['messages'],
	conversationId: string,
	traceId: string | undefined,
	chatContextStoreActions: ChatStoreContainerProps['chatContextStoreActions'],
	experienceTracker: ExperienceTrackerAPI,
	getMessages: ChatStoreContainerProps['getMessages'],
	setMessages: ChatStoreContainerProps['setMessages'],
	sendMessageLatencyMetrics: Metric,
	cancelStreamingMetrics: () => void,
	commonExperienceAttributes: Record<string, any>,
	productKey: string | undefined,
): boolean => {
	// Some products do not require first response to be an action,
	// so exit early when we're not within Confluence
	if (productKey !== 'confluence') {
		return true;
	}

	const humanMessageCount = allMessages.filter(isStoreHuman).length;

	// Only execute on the first human message within the staging area
	if (chatContextStoreActions.getIsStagingArea() && humanMessageCount === 1) {
		const firstAssistantResponse = allMessages.find(isStoreAssistant);
		const firstAction = firstAssistantResponse?.content?.message?.message?.actions?.[0];

		const hasCreatableAction = firstAction ? isCreatableAction(firstAction) : false;

		const authUrl = extractOutboundAuthLink(
			firstAssistantResponse?.content?.message?.message?.content ?? '',
		);

		// Experience handling for the scenario where there
		// should be an action returned but there was not
		if (!hasCreatableAction) {
			// If authUrl is present, abort the experience
			if (authUrl) {
				experienceTracker.abort({
					name: ExperienceName.AIFC_GENERATE_CONTENT,
					reason: 'authentication-required',
					attributes: commonExperienceAttributes,
				});
			} else {
				// Otherwise, fail the experience & show an error in the UI
				const noActionError = new NoActionInvokedError(
					"The content couldn't be created. Please try again",
					{
						traceId: traceId,
					},
				);

				experienceTracker.fail({
					name: ExperienceName.AIFC_GENERATE_CONTENT,
					error: noActionError,
					attributes: {
						...commonExperienceAttributes,
						reason: 'no-action-invoked',
					},
				});

				const messages = getMessages(conversationId);

				// This is to show the error in the UI
				setMessages(conversationId, [...messages, createStoreError(noActionError)]);

				// Stop metrics before returning
				sendMessageLatencyMetrics.cancel();
				cancelStreamingMetrics();

				return false;
			}
		}
	}
	return true;
};

export const processStreamResponseMarkdown = ({
	streamMessage,
	messageId,
	conversationId,
	sendAnalyticsEvent,
}: {
	streamMessage: StreamResponse;
	messageId: string;
	conversationId: string;
	sendAnalyticsEvent: ChatStoreContainerProps['sendAnalyticsEvent'];
}) => {
	let processedStreamMessage = streamMessage;
	const markdownData: Record<string, string> = {};

	if (fg('rovo_enable_data_sculptor')) {
		const dataSculptorMsg = extractMarkdownContent({
			streamMessage: processedStreamMessage,
			boundaryStart: '[^sculptor:start^]',
			boundaryEnd: '[^sculptor:end^]',
			replacementContent: '[^sculptor^]',
		});
		processedStreamMessage = dataSculptorMsg.processedStreamMessage as StreamResponse;
		dataSculptorMsg.boundaryContents.forEach((content, index) => {
			markdownData[`sculptor-${index}`] = content;
		});
	}

	const expandOpenTag = '▞';
	const expandCloseTag = '▚';
	const expandPlaceholder = '[^expand^]';

	const expandMsg = extractMarkdownContent({
		streamMessage: processedStreamMessage,
		boundaryStart: expandOpenTag,
		boundaryEnd: expandCloseTag,
		replacementContent: expandPlaceholder,
	});
	processedStreamMessage = expandMsg.processedStreamMessage as StreamResponse;
	expandMsg.boundaryContents.forEach((content, index) => {
		markdownData[`expand-${index}`] = content;
	});

	if (expandMsg?.boundaryContents && expandMsg.boundaryContents.length > 0) {
		sendAnalyticsEvent({
			eventType: 'track',
			action: 'viewed',
			actionSubject: 'expandComponent',
			attributes: {
				messageId,
				conversationId,
				expandCount: expandMsg.boundaryContents.length,
				timestamp: new Date().toISOString(),
				isFinalResponse: true,
			},
		});
	}

	const chartOpenTag = '◴';
	const chartCloseTag = '◶';
	const chartPlaceholder = '[^chart^]';
	const chartMsg = extractMarkdownContent({
		streamMessage: processedStreamMessage,
		boundaryStart: chartOpenTag,
		boundaryEnd: chartCloseTag,
		replacementContent: chartPlaceholder,
	});
	processedStreamMessage = chartMsg.processedStreamMessage as StreamResponse;
	chartMsg.boundaryContents.forEach((content, index) => {
		markdownData[`chart-${index}`] = content;
	});

	if (chartMsg?.boundaryContents && chartMsg.boundaryContents.length > 0) {
		sendAnalyticsEvent({
			eventType: 'track',
			action: 'extracted',
			actionSubject: 'vizPluginChart',
			attributes: {
				messageId,
				conversationId,
				chartCount: chartMsg.boundaryContents.length,
				timestamp: new Date().toISOString(),
			},
		});
	}

	if (fg('rovo_chat_enable_inline_lumina')) {
		const luminaMsg = extractMarkdownContent({
			streamMessage: processedStreamMessage,
			boundaryStart: '<lumina>',
			boundaryEnd: '</lumina>',
			replacementContent: '\n\n[^lumina^]\n\n',
		});
		processedStreamMessage = luminaMsg.processedStreamMessage as StreamResponse;
		luminaMsg.boundaryContents.forEach((content, index) => {
			markdownData[`lumina-${index}`] = content;
		});
	}

	return {
		processedStreamMessage,
		markdownData,
	};
};

export const addMemorytoLatestAssistantMessage = ({
	chat,
	answerId,
	useIndex,
	memory,
}: {
	chat: StoreChatItem[];
	answerId: string;
	useIndex?: number;
	memory: StoreMemoryUpdate;
}): StoreChatItem[] =>
	updateStoreAssistant({
		chat,
		promptId: answerId,
		updateFunction: (storeAssistant): StoreAssistant => ({
			...storeAssistant,
			memory,
		}),
		updateType: 'Add memory',
		useIndex,
	});

export type ReconnectStreamMeta = {
	citedProductList: Record<string, number>;
	sourceProducts: Record<string, number>;
	pluginsInvoked: string[];
	responseSources?: string[];
	usesJsm?: boolean;
	messageActions?: string[];
	scenarioId?: string;
	skillsInvoked: SkillAttributes[];
	executedStratusSkill: boolean;
};

type InternalTool = {
	name: string;
	/** true if this tool is an action */
	isConsequential: boolean;
};

export type SkillAttributes = {
	/** ex: JiraWorkflowMinion */
	minionId: string;
	/** i.e. skillSlug/skill_alias ex: create-work-items */
	skillName: string | null;
	skillType: 'NATIVE' | 'STRATUS';
	invocationType: 'explicit' | 'implicit';
	internalTools: InternalTool[];
};

// TODO: reuse in processStream (send-message-action.ts)
// to replace the inline trackingAttributes population from FINAL_RESPONSE.
export const extractStreamMeta = (response: StreamResponse): ReconnectStreamMeta => {
	const sources = response.message?.message?.sources;
	const pluginInvocations = response.message?.message?.plugin_invocations;
	const minionOutputs = response.message?.message?.minion_outputs;
	const pluginsInvoked: string[] = [];
	const skillsInvoked: SkillAttributes[] = [];
	let usesJsm = false;

	pluginInvocations?.forEach((pi) => {
		if (pi.plugin_name) {
			pluginsInvoked.push(pi.plugin_name);
		}
		if (
			pi.plugin_output &&
			containsServiceDeskSource(pi.plugin_output) &&
			fg('rovo_chat_analytics_source_extended')
		) {
			usesJsm = true;
		}
	});

	const flatMinionOutputs = minionOutputs?.flat() ?? [];

	const executedStratusSkill =
		flatMinionOutputs.some((output) => output.skill_type === 'STRATUS') ?? false;

	flatMinionOutputs.forEach((output) => {
		pluginsInvoked.push(output.minion_id);
		skillsInvoked.push({
			minionId: output.minion_id,
			skillName: output.skill_alias,
			skillType: output.skill_type,
			invocationType: output.directly_invoked ? 'explicit' : 'implicit',
			internalTools:
				output.internal_tools?.map((tool) => ({
					name: tool.name,
					isConsequential: tool.is_consequential,
				})) ?? [],
		});

		if (
			output.minion_output &&
			containsServiceDeskSource(output.minion_output) &&
			fg('rovo_chat_analytics_source_extended')
		) {
			usesJsm = true;
		}
	});

	return {
		citedProductList: constructCitedProductList(sources),
		sourceProducts: constructSourceProducts(sources),
		pluginsInvoked,
		skillsInvoked,
		executedStratusSkill,
		messageActions: response.message?.message?.actions?.map((action) => action.key),
		responseSources: fg('rovo_chat_analytics_source_extended')
			? sources?.map((x) => x.ari).filter(Boolean)
			: undefined,
		usesJsm: fg('rovo_chat_analytics_source_extended') ? usesJsm : undefined,
		scenarioId: response.message?.message?.scenario?.id,
	};
};
